/* tslint:disable */
/** we can safely disable tslint here as this is a
 * lift and shift from @angular/material
 */
import {
  Attribute,
  Component,
  ElementRef,
  ChangeDetectorRef,
  Input,
  OnDestroy,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  Optional,
  Inject,
} from "@angular/core"
import { ANIMATION_MODULE_TYPE } from "@angular/platform-browser/animations"
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms"
import { FocusMonitor, FocusOrigin } from "@angular/cdk/a11y"
import { coerceBooleanProperty } from "@angular/cdk/coercion"

/**
 * Represents the different states that require custom transitions between them.
 * @docs-private
 */
export enum TransitionCheckState {
  /** The initial state of the component before any user interaction. */
  Init,
  /** The state representing the component when it's becoming checked. */
  Checked,
  /** The state representing the component when it's becoming unchecked. */
  Unchecked,
  /** The state representing the component when it's becoming indeterminate. */
  Indeterminate,
}

let nextUniqueId = 0

export class CheckboxChange {
  source: CheckboxComponent
  checked: boolean

  constructor(checked?: boolean, source?: CheckboxComponent) {
    this.checked = checked
    this.source = source
  }
}

const CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CheckboxComponent),
  multi: true,
}

@Component({
  selector: "c1-privacy-checkbox",
  templateUrl: "./checkbox.component.html",
  styleUrls: ["./checkbox.component.scss"],
  providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR],
  standalone: false,
  /* tslint:disable:no-host-metadata-property */
  host: {
    class: "checkbox",
    "[id]": "id",
    "[attr.tabindex]": "null",
    "[class.checkbox-indeterminate]": "indeterminate",
    "[class.checkbox-checked]": "checked",
    "[class.checkbox-disabled]": "disabled",
    "[class.checkbox-label-before]": 'labelPosition == "before"',
    "[class._animation-noopable]": `_animationMode === 'NoopAnimations'`,
  },
})
export class CheckboxComponent implements ControlValueAccessor, OnDestroy {
  _elementRef: ElementRef<HTMLElement>
  @ViewChild("input") _inputElement: ElementRef<HTMLInputElement>

  private _currentCheckState: TransitionCheckState = TransitionCheckState.Init

  /* tslint:disable:no-output-native */
  @Output() readonly change: EventEmitter<CheckboxChange> = new EventEmitter<CheckboxChange>()

  /** Event emitted when the checkbox's `indeterminate` value changes. */
  @Output() readonly indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>()

  @Input() isParent: boolean

  @Input() tabIndex: number
  @Input() value: string

  /** Name value will be applied to the input element if present */
  @Input()
  name: string | null = null

  /**
   * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
   * take precedence so this may be omitted.
   */
  @Input("aria-label") ariaLabel: string | null = null
  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input("aria-labelledby") ariaLabelledby: string | null = null

  @Input()
  get checked(): boolean {
    return this._checked
  }
  set checked(value: boolean) {
    if (value != this.checked) {
      this._checked = value
      this._changeDetectorRef.markForCheck()
    }
  }
  private _checked: boolean = false

  @Input()
  get disabled() {
    return this._disabled
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value)
    if (newValue !== this.disabled) {
      this._disabled = newValue
      this._changeDetectorRef.markForCheck()
    }
  }
  private _disabled: boolean = false

  @Input()
  get indeterminate(): boolean {
    return this._indeterminate
  }
  set indeterminate(value: boolean) {
    const changed = value != this._indeterminate
    this._indeterminate = value

    if (changed) {
      if (this._indeterminate) {
        this._transitionCheckState(TransitionCheckState.Indeterminate)
      } else {
        this._transitionCheckState(this.checked ? TransitionCheckState.Checked : TransitionCheckState.Unchecked)
      }
      // Defer the emit to avoid ExpressionChangedAfterItHasBeenCheckedError in Angular 21
      // when using two-way binding [(indeterminate)]
      Promise.resolve().then(() => {
        this.indeterminateChange.emit(this._indeterminate)
        this._changeDetectorRef.markForCheck()
      })
    }
  }
  private _indeterminate: boolean = false

  @Input()
  get required(): boolean {
    return this._required
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value)
  }
  private _required: boolean

  private _uniqueId: string = `privacy-checkbox-${++nextUniqueId}`
  @Input() id: string = this._uniqueId
  get inputId(): string {
    return `${this.id || this._uniqueId}-input`
  }

  /** Whether the label should appear after or before the checkbox. Defaults to 'after' */
  @Input()
  labelPosition: "before" | "after" = "after"

  _onTouched: () => any = () => {}

  private _controlValueAccessorChangeFn: (value: any) => void = () => {}

  get inputClass() {
    return "grv-checkbox__input checkbox__input"
  }

  get labelClass() {
    return "grv-checkbox__label checkbox__label"
  }

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor,
    @Attribute("tabindex") tabIndex: string,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) public _animationMode?: string
  ) {
    this.tabIndex = parseInt(tabIndex, 10) || 0
    this._elementRef = elementRef
    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => {
          this._onTouched()
          _changeDetectorRef.markForCheck()
        })
      }
    })
  }

  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef)
  }

  registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn
  }

  registerOnTouched(fn: any) {
    this._onTouched = fn
  }

  writeValue(value: any) {
    this.checked = !!value
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
  }

  toggle(): void {
    this.checked = !this.checked
  }

  _onInputClick(event: Event) {
    event.stopPropagation()

    if (!this.disabled) {
      if (this.indeterminate) {
        Promise.resolve().then(() => {
          this._indeterminate = false
          this.indeterminateChange.emit(this._indeterminate)
        })
      }

      this.toggle()
      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this._emitChangeEvent()
    }
  }

  _getAriaChecked(): "true" | "false" | "mixed" {
    return this.checked ? "true" : this.indeterminate ? "mixed" : "false"
  }

  _onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation()
  }

  /** Focuses the checkbox. */
  focus(origin: FocusOrigin = "keyboard", options?: FocusOptions): void {
    this._focusMonitor.focusVia(this._inputElement, origin, options)
  }

  private _transitionCheckState(newState: TransitionCheckState) {
    const oldState = this._currentCheckState

    if (oldState === newState) {
      return
    }

    this._currentCheckState = newState
  }

  private _emitChangeEvent() {
    const event = new CheckboxChange()
    event.source = this
    event.checked = this.checked

    this._controlValueAccessorChangeFn(this.checked)
    this.change.emit(event)
  }
}
